import React, { useEffect, useMemo, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import {
  AcademicCapIcon,
  ArrowLeftIcon,
  ArrowPathIcon,
  CheckIcon,
  XMarkIcon,
  UserIcon,
} from '@heroicons/react/24/outline';
import apiClient from '../api/client';
import {
  showSuccess,
  showError,
  showWarning,
} from '../utils/alert';
import { FireLoader } from '../components/Loader';

const EMPTY_ASSESSMENTS = {
  affective: [],
  psychomotor: [],
};

const RATING_OPTIONS = [
  { value: '', label: 'Not assessed' },
  { value: '5', label: '5 — Great / Exceptional / Always' },
  { value: '4', label: '4 — Good / Often' },
  { value: '3', label: '3 — Sometimes / Fair / Reasonable' },
  { value: '2', label: '2 — Little / Rarely / Poor' },
  { value: '1', label: '1 — Negligible / Never / Very poor' },
];

export default function StudentGradeEditor() {
  const { id } = useParams();
  const navigate = useNavigate();

  const [activeTab, setActiveTab] = useState('cognitive');
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [refreshing, setRefreshing] = useState(false);

  const [student, setStudent] = useState(null);
  const [subjects, setSubjects] = useState([]);
  const [academicYears, setAcademicYears] = useState([]);

  const [selectedSubject, setSelectedSubject] = useState('');
  const [selectedTerm, setSelectedTerm] = useState('term_1');
  const [selectedYear, setSelectedYear] = useState('');

  const [test1, setTest1] = useState('');
  const [test2, setTest2] = useState('');
  const [existingGrade, setExistingGrade] = useState(null);

  const [assessments, setAssessments] = useState(EMPTY_ASSESSMENTS);
  const [assessmentValues, setAssessmentValues] = useState({});
  const [assessmentLoading, setAssessmentLoading] = useState(false);
  const [submitted, setSubmitted] = useState(false);

  useEffect(() => {
    fetchInitialData();
  }, [id]);

  useEffect(() => {
    if (
      activeTab === 'cognitive' &&
      selectedSubject &&
      selectedYear &&
      student
    ) {
      fetchExistingGrade();
    }
  }, [
    activeTab,
    selectedSubject,
    selectedTerm,
    selectedYear,
    student,
  ]);

  useEffect(() => {
    if (
      activeTab !== 'cognitive' &&
      selectedYear &&
      student
    ) {
      fetchStudentAssessments();
    }
  }, [activeTab, selectedTerm, selectedYear, student]);

  const fetchInitialData = async () => {
    try {
      setLoading(true);

      const [studentRes, subjectsRes] = await Promise.all([
        apiClient.get(`/students/${id}`),
        apiClient.get(`/students/${id}/subjects?skipCache=true`),
      ]);

      const studentData = studentRes.data.data;
      const subjectList = subjectsRes.data.data || [];

      setStudent(studentData);
      setSubjects(subjectList);

      const assignedYear =
        studentData.academicYear || studentData.academic_year;
      const yearId =
        assignedYear?.id || studentData.academic_year_id;

      const yearList = yearId
        ? [
            {
              id: yearId,
              name:
                assignedYear?.name ||
                assignedYear?.year ||
                'Assigned academic year',
              is_current: assignedYear?.is_current,
            },
          ]
        : [];

      setAcademicYears(yearList);
      setSelectedYear(yearList[0]?.id || '');
      setSelectedSubject(subjectList[0]?.id || '');
    } catch (error) {
      console.error('Error loading grade editor:', error);
      await showError({
        title: 'Unable to Load Result Editor',
        text:
          error.response?.data?.message ||
          'Failed to load the student result information.',
      });
    } finally {
      setLoading(false);
    }
  };

  const refreshSubjects = async () => {
    try {
      setRefreshing(true);

      const res = await apiClient.get(
        `/students/${id}/subjects?skipCache=true`,
      );

      const subjectList = res.data.data || [];
      setSubjects(subjectList);

      if (
        subjectList.length &&
        !subjectList.some(
          (subject) =>
            String(subject.id) === String(selectedSubject),
        )
      ) {
        setSelectedSubject(subjectList[0].id);
      }

      if (!subjectList.length) {
        setSelectedSubject('');
      }

      await showSuccess({
        title: 'Subjects Refreshed',
        text: 'The student subject list was refreshed successfully.',
      });
    } catch (error) {
      console.error('Error refreshing subjects:', error);
      await showError({
        title: 'Refresh Failed',
        text:
          error.response?.data?.message ||
          'Failed to refresh the student subject list.',
      });
    } finally {
      setRefreshing(false);
    }
  };

  const fetchExistingGrade = async () => {
    try {
      const res = await apiClient.get(`/grades/student/${id}`, {
        params: {
          subject_id: selectedSubject,
          term: selectedTerm,
          academic_year_id: selectedYear,
        },
      });

      const grade = res.data.data;

      if (grade) {
        setExistingGrade(grade);
        setTest1(
          grade.test_1 === null || grade.test_1 === undefined
            ? ''
            : String(grade.test_1),
        );
        setTest2(
          grade.test_2 === null || grade.test_2 === undefined
            ? ''
            : String(grade.test_2),
        );
      } else {
        setExistingGrade(null);
        setTest1('');
        setTest2('');
      }
    } catch (_) {
      setExistingGrade(null);
      setTest1('');
      setTest2('');
    }
  };

  const fetchStudentAssessments = async () => {
    try {
      setAssessmentLoading(true);

      const res = await apiClient.get(
        `/assessments/students/${id}`,
        {
          params: {
            academic_year_id: selectedYear,
          },
        },
      );

      const data = res.data.data || EMPTY_ASSESSMENTS;
      setAssessments(data);

      const categoryItems =
        activeTab === 'affective'
          ? data.affective || []
          : data.psychomotor || [];

      const values = {};
      categoryItems.forEach((item) => {
        const value = item[selectedTerm];
        values[item.id] =
          value === null || value === undefined
            ? ''
            : String(value);
      });

      setAssessmentValues(values);
    } catch (error) {
      console.error('Error fetching assessments:', error);
      setAssessments(EMPTY_ASSESSMENTS);
      setAssessmentValues({});
      await showError({
        title: 'Unable to Load Assessments',
        text:
          error.response?.data?.message ||
          'Failed to load the student assessments.',
      });
    } finally {
      setAssessmentLoading(false);
    }
  };

  const handleGradeSubmit = async (event) => {
    event.preventDefault();

    if (!selectedSubject || !selectedYear) {
      await showWarning({
        title: 'Required Information',
        text: 'Please select a subject and academic year before saving.',
      });
      return;
    }

    setSaving(true);

    try {
      await apiClient.put('/grades', {
        student_id: id,
        subject_id: selectedSubject,
        term: selectedTerm,
        test_1: test1 === '' ? null : Number(test1),
        test_2: test2 === '' ? null : Number(test2),
        academic_year_id: selectedYear,
      });

      await showSuccess({
        title: existingGrade ? 'Grade Updated' : 'Grade Saved',
        text: existingGrade
          ? 'The cognitive grade was updated successfully.'
          : 'The cognitive grade was saved successfully.',
      });
      setSubmitted(true);
      await fetchExistingGrade();
      setTimeout(() => setSubmitted(false), 2500);
    } catch (error) {
      console.error('Error saving grade:', error);
      await showError({
        title: 'Grade Save Failed',
        text:
          error.response?.data?.message ||
          'Failed to save the cognitive grade.',
      });
    } finally {
      setSaving(false);
    }
  };

  const handleAssessmentSubmit = async (event) => {
    event.preventDefault();

    if (!selectedYear) {
      await showWarning({
        title: 'Academic Year Required',
        text: 'Please select an academic year before saving.',
      });
      return;
    }

    const items =
      activeTab === 'affective'
        ? assessments.affective || []
        : assessments.psychomotor || [];

    if (!items.length) {
      await showWarning({
        title: 'No Assessment Criteria',
        text: 'No assessment criteria are available for this section.',
      });
      return;
    }

    setSaving(true);

    try {
      await apiClient.put(`/assessments/students/${id}`, {
        academic_year_id: selectedYear,
        term: selectedTerm,
        assessments: items.map((item) => ({
          trait_id: item.id,
          rating:
            assessmentValues[item.id] === '' ||
            assessmentValues[item.id] === undefined
              ? null
              : Number(assessmentValues[item.id]),
        })),
      });

      const assessmentLabel =
        activeTab === 'affective'
          ? 'Affective'
          : 'Psychomotor';

      await showSuccess({
        title: `${assessmentLabel} Assessment Saved`,
        text: `${assessmentLabel} ratings were saved successfully.`,
      });

      setSubmitted(true);
      await fetchStudentAssessments();
      setTimeout(() => setSubmitted(false), 2500);
    } catch (error) {
      console.error('Error saving assessment:', error);
      await showError({
        title: 'Assessment Save Failed',
        text:
          error.response?.data?.message ||
          'Failed to save the student assessment.',
      });
    } finally {
      setSaving(false);
    }
  };

  const currentAssessmentItems = useMemo(
    () =>
      activeTab === 'affective'
        ? assessments.affective || []
        : assessments.psychomotor || [],
    [activeTab, assessments],
  );

  const clearCurrentForm = () => {
    if (activeTab === 'cognitive') {
      setTest1('');
      setTest2('');
      return;
    }

    const cleared = {};
    currentAssessmentItems.forEach((item) => {
      cleared[item.id] = '';
    });
    setAssessmentValues(cleared);
  };

  if (loading) {
    return (
      <FireLoader
        fullScreen
        size="lg"
        text="Loading student assessment..."
      />
    );
  }

  if (!student) {
    return (
      <div className="py-12 text-center">
        <p className="text-gray-500">Student not found</p>
        <button
          type="button"
          onClick={() => navigate('/app/students')}
          className="mt-4 rounded-xl bg-primary-600 px-4 py-2 text-white hover:bg-primary-700"
        >
          Back to Students
        </button>
      </div>
    );
  }

  const fullName = [
    student.first_name,
    student.middle_name,
    student.last_name,
  ]
    .filter(Boolean)
    .join(' ');

  return (
    <div className="mx-auto max-w-5xl space-y-6 px-4 py-6 sm:px-6 lg:px-8">
      <div className="flex items-center gap-4">
        <button
          type="button"
          onClick={() => navigate(`/app/students/${id}`)}
          className="rounded-xl p-2 text-gray-600 transition-colors hover:bg-gray-100"
        >
          <ArrowLeftIcon className="h-5 w-5 sm:h-6 sm:w-6" />
        </button>

        <div>
          <h1 className="text-xl font-bold text-gray-800 sm:text-2xl">
            Student Result Assessment
          </h1>
          <p className="text-sm text-gray-500">
            Enter cognitive marks, affective ratings and psychomotor ratings.
          </p>
        </div>
      </div>

      <div className="flex flex-col gap-4 rounded-xl border border-gray-100 bg-white p-4 shadow-sm sm:flex-row sm:items-center sm:p-6">
        <div className="flex h-12 w-12 flex-shrink-0 items-center justify-center rounded-full bg-primary-50 text-primary-600">
          <UserIcon className="h-6 w-6" />
        </div>

        <div className="flex-1">
          <p className="font-medium text-gray-800">
            {fullName}
          </p>

          <div className="flex flex-wrap gap-3 text-sm text-gray-500">
            <span>
              Admission:{' '}
              <span className="font-mono">
                {student.admission_number || 'N/A'}
              </span>
            </span>

            {student.class && (
              <span>
                Class:{' '}
                <span className="font-medium">
                  {student.class.name}
                </span>
              </span>
            )}
          </div>
        </div>

        <div className="text-sm text-gray-400">
          <AcademicCapIcon className="mr-1 inline h-5 w-5" />
          Result Editor
        </div>
      </div>

      <div className="rounded-xl border border-gray-100 bg-white p-2 shadow-sm">
        <div className="grid grid-cols-1 gap-2 sm:grid-cols-3">
          {[
            ['cognitive', 'Cognitive Grades'],
            ['affective', 'Affective Assessment'],
            ['psychomotor', 'Psychomotor Assessment'],
          ].map(([key, label]) => (
            <button
              key={key}
              type="button"
              onClick={() => setActiveTab(key)}
              className={`rounded-lg px-4 py-2.5 text-sm font-medium transition-colors ${
                activeTab === key
                  ? 'bg-primary-600 text-white'
                  : 'text-gray-600 hover:bg-gray-50'
              }`}
            >
              {label}
            </button>
          ))}
        </div>
      </div>

      <div className="rounded-xl border border-gray-100 bg-white p-4 shadow-sm sm:p-6">
        <div className="mb-6 grid grid-cols-1 gap-4 sm:grid-cols-2">
          <div>
            <label className="mb-1 block text-sm font-medium text-gray-700">
              Term
            </label>
            <select
              value={selectedTerm}
              onChange={(event) =>
                setSelectedTerm(event.target.value)
              }
              className="w-full rounded-lg border border-gray-200 bg-gray-50/50 px-4 py-2.5 text-sm text-gray-700 outline-none focus:border-primary-400 focus:ring-2 focus:ring-primary-100"
            >
              <option value="term_1">Term 1</option>
              <option value="term_2">Term 2</option>
              <option value="term_3">Term 3</option>
            </select>
          </div>

          <div>
            <label className="mb-1 block text-sm font-medium text-gray-700">
              Academic Year
            </label>
            <select
              value={selectedYear}
              onChange={(event) =>
                setSelectedYear(event.target.value)
              }
              className="w-full rounded-lg border border-gray-200 bg-gray-50/50 px-4 py-2.5 text-sm text-gray-700 outline-none focus:border-primary-400 focus:ring-2 focus:ring-primary-100"
              required
            >
              <option value="">Select Year</option>
              {academicYears.map((year) => (
                <option key={year.id} value={year.id}>
                  {year.name}
                </option>
              ))}
            </select>
          </div>
        </div>

        {activeTab === 'cognitive' ? (
          <form onSubmit={handleGradeSubmit} className="space-y-6">
            <div>
              <div className="flex items-center justify-between">
                <label className="mb-1 block text-sm font-medium text-gray-700">
                  Subject
                </label>

                <button
                  type="button"
                  onClick={refreshSubjects}
                  disabled={refreshing}
                  className="flex items-center gap-1 text-xs text-primary-600 hover:text-primary-700"
                >
                  <ArrowPathIcon
                    className={`h-3 w-3 ${
                      refreshing ? 'animate-spin' : ''
                    }`}
                  />
                  Refresh
                </button>
              </div>

              <select
                value={selectedSubject}
                onChange={(event) =>
                  setSelectedSubject(event.target.value)
                }
                className="w-full rounded-lg border border-gray-200 bg-gray-50/50 px-4 py-2.5 text-sm text-gray-700 outline-none focus:border-primary-400 focus:ring-2 focus:ring-primary-100"
                required
              >
                <option value="">Select Subject</option>
                {subjects.map((subject) => (
                  <option key={subject.id} value={subject.id}>
                    {subject.name}
                  </option>
                ))}
              </select>

              {!subjects.length && (
                <p className="mt-1 text-xs text-amber-600">
                  No subjects are assigned to this student's class.
                </p>
              )}
            </div>

            <div className="rounded-lg border border-blue-100 bg-blue-50 px-4 py-3 text-sm text-blue-700">
              Enter only Test 1 and Test 2 marks. Examination marks are not
              required on this continuous-assessment page.
            </div>

            <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
              {[
                ['Test 1', test1, setTest1],
                ['Test 2', test2, setTest2],
              ].map(([label, value, setter]) => (
                <div key={label}>
                  <label className="mb-1 block text-sm font-medium text-gray-700">
                    {label}
                  </label>
                  <input
                    type="number"
                    min="0"
                    max="100"
                    value={value}
                    onChange={(event) =>
                      setter(event.target.value)
                    }
                    className="w-full rounded-lg border border-gray-200 bg-gray-50/50 px-4 py-2.5 text-sm text-gray-700 outline-none focus:border-primary-400 focus:ring-2 focus:ring-primary-100"
                    placeholder="Not posted"
                  />
                </div>
              ))}
            </div>

            {existingGrade && (
              <div className="flex items-center gap-2 rounded-lg border border-green-200 bg-green-50 px-4 py-2.5 text-sm text-green-700">
                <CheckIcon className="h-5 w-5 text-green-500" />
                Existing grade loaded. Saving will update it.
              </div>
            )}

            <ActionButtons
              saving={saving}
              disabled={!subjects.length}
              submitLabel="Save Grade"
              clear={clearCurrentForm}
            />
          </form>
        ) : (
          <form
            onSubmit={handleAssessmentSubmit}
            className="space-y-6"
          >
            <div>
              <h2 className="text-base font-semibold text-gray-800">
                {activeTab === 'affective'
                  ? 'Affective Ratings'
                  : 'Psychomotor Ratings'}
              </h2>
              <p className="mt-1 text-sm text-gray-500">
                Rate each item from 1 to 5. Leave it blank when the
                student has not yet been assessed.
              </p>
            </div>

            {assessmentLoading ? (
              <div className="py-10 text-center text-sm text-gray-500">
                Loading assessment criteria...
              </div>
            ) : (
              <div className="overflow-hidden rounded-xl border border-gray-200">
                <div className="hidden grid-cols-[90px_1fr_290px] gap-3 bg-gray-50 px-4 py-3 text-xs font-semibold uppercase tracking-wide text-gray-500 md:grid">
                  <div>Code</div>
                  <div>Assessment criterion</div>
                  <div>Rating</div>
                </div>

                {currentAssessmentItems.map((item) => (
                  <div
                    key={item.id}
                    className="grid grid-cols-1 gap-3 border-t border-gray-100 px-4 py-3 first:border-t-0 md:grid-cols-[90px_1fr_290px] md:items-center"
                  >
                    <div className="text-sm font-semibold text-gray-700">
                      {item.code}
                    </div>

                    <div className="text-sm text-gray-700">
                      {item.name}
                    </div>

                    <select
                      value={assessmentValues[item.id] ?? ''}
                      onChange={(event) =>
                        setAssessmentValues((current) => ({
                          ...current,
                          [item.id]: event.target.value,
                        }))
                      }
                      className="w-full rounded-lg border border-gray-200 bg-gray-50/50 px-3 py-2 text-sm text-gray-700 outline-none focus:border-primary-400 focus:ring-2 focus:ring-primary-100"
                    >
                      {RATING_OPTIONS.map((option) => (
                        <option
                          key={option.value || 'blank'}
                          value={option.value}
                        >
                          {option.label}
                        </option>
                      ))}
                    </select>
                  </div>
                ))}

                {!currentAssessmentItems.length && (
                  <div className="px-4 py-10 text-center text-sm text-gray-500">
                    No assessment criteria are available.
                  </div>
                )}
              </div>
            )}

            <ActionButtons
              saving={saving}
              disabled={
                assessmentLoading ||
                !currentAssessmentItems.length
              }
              submitLabel={`Save ${
                activeTab === 'affective'
                  ? 'Affective'
                  : 'Psychomotor'
              } Ratings`}
              clear={clearCurrentForm}
            />
          </form>
        )}

        {submitted && (
          <div className="mt-4 flex items-center gap-2 rounded-lg border border-blue-200 bg-blue-50 px-4 py-2.5 text-sm text-blue-700">
            <CheckIcon className="h-5 w-5 text-blue-500" />
            Saved successfully.
          </div>
        )}
      </div>

      <div className="text-center text-xs text-gray-400">
        Rating guide: 5 = Great/Always, 4 = Good/Often,
        3 = Sometimes/Fair, 2 = Little/Rarely, 1 = Negligible/Never.
      </div>
    </div>
  );
}

function ActionButtons({
  saving,
  disabled,
  submitLabel,
  clear,
}) {
  return (
    <div className="flex flex-wrap gap-3 border-t border-gray-100 pt-4">
      <button
        type="submit"
        disabled={saving || disabled}
        className="inline-flex items-center justify-center rounded-lg bg-primary-600 px-6 py-2.5 text-sm font-medium text-white shadow-sm transition-colors hover:bg-primary-700 disabled:cursor-not-allowed disabled:opacity-50"
      >
        {saving ? (
          <>
            <span className="mr-2 h-4 w-4 animate-spin rounded-full border-2 border-white border-t-transparent" />
            Saving...
          </>
        ) : (
          <>
            <CheckIcon className="mr-2 h-5 w-5" />
            {submitLabel}
          </>
        )}
      </button>

      <button
        type="button"
        onClick={clear}
        className="inline-flex items-center justify-center rounded-lg bg-gray-100 px-6 py-2.5 text-sm font-medium text-gray-700 transition-colors hover:bg-gray-200"
      >
        <XMarkIcon className="mr-2 h-5 w-5" />
        Clear
      </button>
    </div>
  );
}